import { useModal } from '@jutro/components';
import { BreakpointTrackerContext } from '@jutro/layout';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import { useDependencies } from '@xengage/gw-portals-dependency-react';
import { ViewModelForm } from '@xengage/gw-portals-viewmodel-react';
import { WizardPage, wizardProps } from '@xengage/gw-portals-wizard-react';
import { UWIssuesUtil } from "premier-policycommon-util-js";
import _ from 'lodash';
import React, { useCallback, useContext, useEffect } from 'react';
import PrintDocumentsComponent from "premier-capability-policyjob-react/components/PrintDocumentsComponent/PrintDocumentsComponent";
import FormsComponent from "premier-capability-policyjob-react/components/FormComponent/FormComponent";
import { PremierConstants } from 'premier-constant-service';
import QuoteValidityComponent from "premier-capability-policyjob-react/components/QuoteValidityComponent/QuoteValidityComponent";
import { useTranslator } from '@jutro/locale';
import QuoteDetailsComponent from 'premier-capability-policyjob-react/components/QuoteDetailsComponent/QuoteDetailsComponent';
import setErrorModalFromWhitelist from '../../config/WhitelistedErrors';
import messages from './QuotePage.messages';
import presentationMetadata from './QuotePage.metadata.json5';
import styles from './QuotePage.module.scss';
import CoveragesPage from '../Coverages/CoveragesPage';

function QuotePage(props) {
    const { authHeader } = useAuthentication();
    const {
        wizardData: submissionVM,
        steps,
        jumpTo,
        currentStep,
        wizardData,
        updateWizardData,
        underwritingIssues
    } = props;

    // const { showAlert } = useModal();
    // const breakpoint = useContext(BreakpointTrackerContext);
    // const { LoadSaveService } = useDependencies('LoadSaveService');
    // const { pageKey } = currentStep;

    const breakpoint = useContext(BreakpointTrackerContext);
    const { pageKey } = currentStep;
    const isQuoteBlocked = UWIssuesUtil.isQuoteBlocked(underwritingIssues);
    const findBlockingPoint = UWIssuesUtil.findBlockingPoint(underwritingIssues);
    const underWritingIssuesNonBlocking = UWIssuesUtil.UWIssuesNonBlocking(underwritingIssues);
    const translator = useTranslator();
    const metadata = presentationMetadata[pageKey];
    const { lineName } = PremierConstants;
    const productCode = _.get(submissionVM.value, 'baseData.productCode');
    // eslint-disable-next-line security/detect-object-injection
    const lobLineName = productCode ? lineName[productCode] : '';

    const handleNext = useCallback(() => {
        const offeredQuotes = _.get(submissionVM.value, 'quoteData.offeredQuotes[0].publicID');

        _.set(submissionVM, 'bindData.chosenQuote', offeredQuotes);

        return submissionVM;
    }, [submissionVM]);

    const navigateToCoverages = useCallback(() => {

        const selectOffering = submissionVM.value.lobData.eveLiaLine.selectedOffering_Ext
        let redirectPath = ''

        switch (selectOffering) {
            case 'FooRef':
                redirectPath = '/liabilityCoveragesPage';
                break;
            default:
                break;
        }

        const indexOfGeneralCoverages = _.findIndex(
            steps,
            ({ path }) => path === redirectPath
        );

        jumpTo(indexOfGeneralCoverages);
    }, [jumpTo, steps, submissionVM]);

    // eslint-disable-next-line arrow-body-style
    const getCell = (item, _index, property) => {
        return item[property.id];
    };

    const getInternalUseOnlyConditionsOfQuoteTableData = () => _.map(submissionVM.value.lobData.eveLiaLine.coverables.foodTruckCoverable.internalUseOnly.plesubofQuoExps, (plesubofQuoExp) => ({
        conFieConditionsOfQuote: plesubofQuoExp
    }));

    const getInternalUseOnlyAdditionalConditionsTableData = () => _.map(submissionVM.value.lobData.eveLiaLine.coverables.foodTruckCoverable.internalUseOnly.pleaddConofPolExps, (pleaddConofPolExp) => ({
        conFieAdditionalConditions: pleaddConofPolExp
    }));

    const resolvers = {
        resolveClassNameMap: styles,
        resolveCallbackMap: {
            navigateToCoverages,
            getCell
        },
        resolveComponentMap: {
            coveragespagecomponent: CoveragesPage,
            quoteDetailsComponent: QuoteDetailsComponent,
            quoteValidityComponent: QuoteValidityComponent,
            PrintDocumentsComponent,
            FormsComponent
        }
    };

    const getAccordianTitle = useCallback((index) => (
        <div className={styles.accordianTitle}>
            <p>
            {_.get(
                submissionVM.value,
                `lobData.eveLiaLine.coverables.locRisks[${index}].accountLocFie.displayName`
            )}
            </p>
        </div>
    ),[submissionVM]);

    const generatelocRiskseOverrides = () => {

        const overrides = submissionVM.value.lobData.eveLiaLine.coverables.locRisks.map((locRisk, index) => ({
            [`locRiskSection${index}`]: {
                title: locRisk.accountLocFie.displayName
            },
            [`locRiskCoverageInfoComponent${index}`]: {
                wizardData,
                updateWizardData,
                clausesBasePath: `lobData.eveLiaLine.lineCoverages.locRiskCoverages.children[${index}].coverages`,
                title: "Coverages",
                clasueType: 'coverages',
                isEditable: false
            },
            [`locRiskCoverageComponent${index}`]: {
                wizardData: submissionVM,
                updateWizardData,
                clausesBasePath: `lobData.eveLiaLine.lineCoverages.locRiskCoverages.plecoverages.children[${index}].coverages`,
                title: getAccordianTitle(index),
                clasueType: 'coverages',
                pathToGet: `eveLiaLine.locRiskCoverages.plecoverages[${index}].coverages`,
                isEditable: false
            },
            [`propertyCoveragesAccordionDetails${index}`]: {                     
                title: getAccordianTitle(index),
                className: styles.gwOuterLocationAccordion
            },
            [`propertyCoveragesAccordion${index}`]: {
                className: styles.gwAccordionSet
            },
        }));

        return Object.assign({}, ...overrides);
    };

    const overrideProps = {
        '@field': {
            labelPosition: breakpoint === 'desktop' ? 'left' : 'top',
        },
        underWritingIssues: {
            underwritingIssues: underWritingIssuesNonBlocking,
            blockQuote: findBlockingPoint,
            filterUWIssuesInCustomOffering: false,
            quoteID: _.get(submissionVM.value, 'quoteID'),
            accountNumber: _.get(submissionVM.value, 'baseData.accountNumber')
        },
        liabilityCoverageInfoComponent: {
            wizardData,
            updateWizardData,
            clausesBasePath: `lobData.eveLiaLine.lineCoverages.coverages`,
            title: "Coverages",
            clasueType: 'coverages',
            isEditable: false
        },
        propertyAccordion: {
            visible: !_.isUndefined(_.get(wizardData.value, 'lobData.eveLiaLine.lineCoverages.locRiskCoverages'))
                && _.get(wizardData.value, 'lobData.eveLiaLine.lineCoverages.locRiskCoverages.plecoverages').length > 0
        },
        quoteDetailsComponent: {
            wizardData
        },
        quoteValidityComponent: {
            wizardData
        },
        policyQuoteValidityDays: {
            content: _.get(submissionVM, `baseData.quoteValidityDays_Ext.value`)
        },
        policyQuoteValDaysHead: {
            content: translator(messages.plQuoteValidityDays, {
                quoteValidityDays: _.get(submissionVM, `baseData.quoteValidityDays_Ext.value`)
            })
        },
        tableInternalUseOnlyConditionsOfQuote: {
            data: getInternalUseOnlyConditionsOfQuoteTableData()
        },
        tableInternalUseOnlyAdditionalConditions: {
            data: getInternalUseOnlyAdditionalConditionsTableData()
        },
        internalUseOnlyAccordion: {
            visible: submissionVM.value.lobData.eveLiaLine.coverables.foodTruckCoverable.internalUseOnly.pleaddConofPolExps.length > 0
        },
        internalUseOnlyConditionsOfQuoteContainer: {
            visible: submissionVM.value.lobData.eveLiaLine.coverables.foodTruckCoverable.internalUseOnly.plesubofQuoExps.length > 0
        },
        internalUseOnlyAdditionalConditionsContainer: {
            visible: submissionVM.value.lobData.eveLiaLine.coverables.foodTruckCoverable.internalUseOnly.pleaddConofPolExps.length > 0
        },
        taxes: {
            visible: _.get(submissionVM.value, 'baseData.baseState_Ext') === 'QC',
        },
        fees: {
            visible: _.get(submissionVM.value, 'quoteData.offeredQuotes') &&
                _.get(submissionVM.value, 'quoteData.offeredQuotes[0].premium.fees.amount') !== 0
        },
        policyfees: {
            visible: _.get(submissionVM.value, 'baseData.policyFee_Ext.amount') !== 0
        },
        inspectionFee: {
            visible: _.get(submissionVM.value, 'baseData.inspectionFee_Ext.amount') !== 0
        },
        extensionFee: {
            visible: _.get(submissionVM.value, 'baseData.extensionFee_Ext.amount') !== 0
        },
        PrintDocumentsComponent: {
            visible: submissionVM.value.baseData.periodStatus === 'Quoted',
            jobNumber: submissionVM.value.quoteID,
            offeringName: _.get(submissionVM.value, `lobData.${lobLineName}.selectedOffering_Ext`),
            authHeader
        },
        FormsComponent: {
            forms: submissionVM.value.lobData.eveLiaLine.lineCoverages.forms_Ext
        },
        ...generatelocRiskseOverrides()
    };

    useEffect(() => { }, []);

    return (
        <WizardPage
            disableNext={underWritingIssuesNonBlocking?.length > 0}
            onNext={handleNext}
        >
            <ViewModelForm
                uiProps={metadata.content}
                model={submissionVM}
                overrideProps={overrideProps}
                callbackMap={resolvers.resolveCallbackMap}
                classNameMap={resolvers.resolveClassNameMap}
                componentMap={resolvers.resolveComponentMap}
            />
        </WizardPage>
    );
}

QuotePage.propTypes = wizardProps;
export default QuotePage;
